import { Injectable, signal, inject } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';

/**
 * Gates app access to super-admins only. Authentication alone is not enough —
 * after a Firebase sign-in we confirm the caller is a super-admin venue manager
 * via the backend `superAdmin-amISuperAdmin` callable, which runs the same
 * `checkSuperAdminVenueManagerRequestContext` precondition every other
 * super-admin endpoint uses (source of truth:
 * `super_admin_permissions/venue_management.users[]`).
 */
@Injectable({ providedIn: 'root' })
export class AuthGateService {
  private functions = inject(Functions);

  // Set when a signed-in user is rejected for not being a super-admin, so the
  // login screen can explain why they were signed back out.
  readonly notAuthorized = signal(false);

  async isSuperAdmin(): Promise<boolean> {
    try {
      const callable = httpsCallable<unknown, AmISuperAdminResult>(
        this.functions,
        'superAdmin-amISuperAdmin',
      );
      const result = await callable({});
      // amISuperAdmin is wrapped in the backend processTransaction envelope, so
      // the callable payload is { success, data: { ok, uid }, ... } — the flag
      // lives at result.data.data.ok (NOT result.data.ok).
      return result.data?.data?.ok === true;
    } catch {
      // Any rejection (unauthenticated/permission) means "not a super-admin".
      // The endpoint rejects rather than returning { ok: false }.
      return false;
    }
  }
}

/** processTransaction envelope returned by superAdmin-amISuperAdmin. */
interface AmISuperAdminResult {
  success?: boolean;
  data?: { ok?: boolean; uid?: string };
  duration?: number;
  timestamp?: string;
}
